import { Button, FormScope, Select, TextField } from '../forms';
import { useDraft } from '../recovery/useDraft';
import { DraftNotice } from '../recovery/DraftNotice';
import { movementDraftValid, type MovementDraft, type MovementRow } from './draft-shapes';
import { useState } from 'react';
import { Banner, BlockStack, Card, Checkbox, InlineStack } from '@shopify/polaris';
import { ResourceChoice } from './ResourceChoice';
import { transit, unshipped, type Transfer, type TransferItem } from './types';
import type { TransferClient } from './useTransferClient';
import { transferIdentityPayload } from '../../../../packages/shared/transfers/identity-draft';
import { TransferIdentities } from './TransferIdentities';
export function TransferMovement({
  client,
  transfer,
  items,
}: {
  client: TransferClient;
  transfer: Transfer;
  items: TransferItem[];
}) {
  const draft = useDraft<MovementDraft>(client.draftKey + 'transfer-movement:' + transfer.id + ':' + (items[0]?.id || 0), String(transfer.revision), { action: 'ship', rows: {}, reason: '', carrier: transfer.carrier || '', tracking: transfer.tracking_number || '' }, movementDraftValid);
  const [action, setAction] = draft.field('action'), [rows, setRows] = draft.field('rows'), [reason, setReason] = draft.field('reason'), [carrier, setCarrier] = draft.field('carrier'), [tracking, setTracking] = draft.field('tracking');
  const [review, setReview] = useState(false);
  const [error, setError] = useState('');
  const locked = client.locked || review || draft.locked;
  const limit = (item: TransferItem) =>
    action === 'ship' ? Math.max(0, Math.min(unshipped(item), item.available)) : transit(item);
  const update = (id: number, value: Partial<MovementRow>) => setRows({ ...rows, [id]: { ...rows[id], ...value } });
  const selected = items.filter((item) => rows[item.id]);
  const validate = () => {
    if (!selected.length) return setError('Choose at least one item from this page.');
    for (const item of selected) {
      const row = rows[item.id];
      const good = Number(row.quantity);
      const damaged = action === 'receive' ? Number(row.damaged) : 0;
      if (
        !row.quantity.trim() ||
        !Number.isSafeInteger(good) ||
        good < 0 ||
        !Number.isSafeInteger(damaged) ||
        damaged < 0 ||
        good + damaged < 1 ||
        good + damaged > limit(item)
      )
        return setError('Enter whole quantities within the amount available for each selected line.');
      if (good > 0 && ['receive', 'return'].includes(action) && !row.location)
        return setError('Choose a bin for the usable goods.');
      if (damaged > 0 && !row.holding) return setError('Choose a holding bin for damaged goods.');
      if (!item.untracked) {
        try { transferIdentityPayload(row.identities, action, good, damaged); } catch (e) { return setError((e as Error).message); }
      }
    }
    if (
      (['return', 'loss'].includes(action) || selected.some((item) => Number(rows[item.id].damaged) > 0)) &&
      reason.trim().length < 3
    )
      return setError('Give a reason of at least three characters.');
    setError('');
    setReview(true);
  };
  const save = async () => {
    const saved = await client.submit(`/${transfer.id}/${action}`, {
      revision: transfer.revision,
      reason,
      carrier,
      tracking_number: tracking,
      lines: selected.map((item) => ({
        item_id: item.id,
        quantity: Number(rows[item.id].quantity),
        damaged: action === 'receive' ? Number(rows[item.id].damaged) : 0,
        location_id: rows[item.id].location?.id || null,
        holding_location_id: rows[item.id].holding?.id || null,
        ...(!item.untracked ? transferIdentityPayload(rows[item.id].identities, action, Number(rows[item.id].quantity), action === 'receive' ? Number(rows[item.id].damaged) : 0) : {}),
      })),
    }, draft.reference());
    if (saved) draft.clear();
  };
  return (
    <FormScope><Card>
      <BlockStack gap="400">
        <h2>Record a physical movement</h2>
        <DraftNotice draft={draft} locked={client.locked} />
        <p>
          Only record goods that have physically moved. Choose items from the current page. Other lines keep their
          current quantities.
        </p>
        <Select
          label="Movement"
          value={action}
          disabled={locked}
          onChange={(value) => {
            setAction(value as MovementDraft['action']);
            setRows({});
            setError('');
          }}
          options={[
            { label: 'Ship from source warehouse', value: 'ship' },
            { label: 'Receive at destination warehouse', value: 'receive' },
            { label: 'Return in-transit goods to source', value: 'return' },
            { label: 'Record confirmed loss in transit', value: 'loss' },
          ]}
        />
        {action === 'loss' && (
          <Banner tone="warning">
            Only use this after the carrier confirms the goods are lost. It closes those in-transit units without adding
            stock to a warehouse.
          </Banner>
        )}
        {items.map((item) => {
          const row = rows[item.id];
          return (
            <div key={item.id}>
              <Checkbox
                label={`${item.product_sku || item.product_title} from ${item.from_location_name || 'Unknown bin'}: ${limit(item)} units available for this action`}
                checked={!!row}
                disabled={locked || limit(item) < 1}
                onChange={(checked) => {
                  if (checked)
                    update(item.id, {
                      quantity: '',
                      damaged: '0',
                      location:
                        action === 'receive' && item.to_location_id
                          ? { id: item.to_location_id, label: item.to_location_name || 'Planned bin' }
                          : null,
                      holding: null,
                    });
                  else {
                    const next = { ...rows };
                    delete next[item.id];
                    setRows(next);
                  }
                }}
              />
              {row && (
                <BlockStack gap="300">
                  {!item.untracked && <TransferIdentities client={client} transfer={transfer.id} item={item.id} revision={transfer.revision} action={action} value={row.identities} onChange={identities => update(item.id, { identities })} disabled={locked}/>}
                  <TextField
                    label={`${action === 'receive' ? 'Usable' : 'Moved'} quantity for ${item.product_sku || item.product_title}`}
                    type="number"
                    min={action === 'receive' ? 0 : 1}
                    max={limit(item)}
                    step={1}
                    requiredIndicator value={row.quantity}
                    onChange={(quantity) => update(item.id, { quantity })}
                    disabled={locked}
                    autoComplete="off"
                  />
                  {['receive', 'return'].includes(action) && (
                    <ResourceChoice
                      client={client}
                      required={Number(row.quantity) > 0} label="Usable goods bin"
                      binKind={item.untracked ? undefined : 'stock'}
                      warehouse={action === 'return' ? transfer.from_warehouse_id : transfer.to_warehouse_id}
                      value={row.location}
                      onChange={(location) => update(item.id, { location })}
                      disabled={locked}
                    />
                  )}
                  {action === 'receive' && (
                    <>
                      <TextField
                        label={`Damaged quantity for ${item.product_sku || item.product_title}`}
                        type="number"
                        min={0}
                        max={limit(item)}
                        step={1}
                        requiredIndicator value={row.damaged}
                        onChange={(damaged) => update(item.id, { damaged })}
                        disabled={locked}
                        autoComplete="off"
                      />
                      {Number(row.damaged) > 0 && (
                        <ResourceChoice
                          client={client}
                          required={Number(row.damaged) > 0} label="Damaged goods holding bin"
                          binKind={item.untracked ? undefined : 'holding'}
                          warehouse={transfer.to_warehouse_id}
                          value={row.holding}
                          onChange={(holding) => update(item.id, { holding })}
                          disabled={locked}
                        />
                      )}
                    </>
                  )}
                </BlockStack>
              )}
            </div>
          );
        })}
        {!items.length && <p>No items on this page.</p>}
        {action === 'ship' && (
          <>
            <TextField
              label="Carrier or own van reference"
              value={carrier}
              onChange={setCarrier}
              maxLength={100}
              disabled={locked}
              autoComplete="off"
            />
            <TextField
              label="Tracking number (optional)"
              value={tracking}
              onChange={setTracking}
              maxLength={200}
              disabled={locked}
              autoComplete="off"
            />
          </>
        )}
        <TextField
          label="Movement reason"
          requiredIndicator={action === 'loss' || action === 'return' || Object.values(rows).some(row => Number(row.damaged) > 0)} value={reason}
          onChange={setReason}
          maxLength={500}
          disabled={locked}
          autoComplete="off"
          helpText="Required for damaged arrivals, returned goods and confirmed losses."
        />
        {error && <Banner tone="critical">{error}</Banner>}
        {review ? (
          <>
            <h3>Confirm {action}</h3>
            {selected.map((item) => (
              <p key={item.id}>
                {item.product_sku || item.product_title}: {rows[item.id].quantity}{' '}
                {action === 'receive' ? 'usable units' : 'units'}
                {rows[item.id].location ? ' to ' + rows[item.id].location?.label : ''}
                {action === 'receive' && Number(rows[item.id].damaged) > 0
                  ? `; ${rows[item.id].damaged} damaged to ${rows[item.id].holding?.label}`
                  : ''}
                .
              </p>
            ))}
            <InlineStack gap="200">
              <Button variant="primary" disabled={client.locked || draft.locked || !!draft.error} loading={client.busy} onClick={save}>
                Confirm {action}
              </Button>
              <Button disabled={client.locked || draft.locked || !!draft.error} onClick={() => setReview(false)}>
                Change movement
              </Button>
            </InlineStack>
          </>
        ) : (
          <Button validate variant="primary" disabled={client.locked || !selected.length} onClick={validate}>
            Review movement
          </Button>
        )}
      </BlockStack>
    </Card></FormScope>
  );
}
